/*
 * The StepCustomer component is the step in reseller SO flow enabling customer selection.
 * 
 * This takes the following props:
 *   auth                     - authentication object
 *   history                  - for navigation to non-SO flow.  TODO: refactor when moving to react router 6, consider removing functionality
 *   useProjects              - indicate if projects are used in the SO flow.  TODO: refactor when multiple projects are enabled
 *   customer                 - already selected customer or null
 *   setParentStatesChanged   - passthrough to parent state set; takes a set of values that should be set in the same call
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React, { Component } from 'react';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import CustomerCreateDialog from '../customer/CustomerCreateDialog';
import CustomerCreateForm from '../customer/CustomerCreateForm';

import BudgetChangeConfirmationDialog from './BudgetChangeConfirmationDialog';

import AbEnvironment from '../auth/AbEnvironment';
import CustomerFetch from '../customer/CustomerFetch';
import CustomerFetchCountAndMaps from '../customer/CustomerFetchCountAndMaps';
import withStyles from '@mui/styles/withStyles';
import { Avatar, Button, Chip, Divider, Grid, MenuItem, Paper, 
         TextField, Tooltip, Typography
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import AppBindStyles from '../../AppBindStyles';
import DefaultProjectFetch from './DefaultProjectFetch';

const abEnvironment = new AbEnvironment();

function renderInputComponent(inputProps) {
   const { classes, inputRef = () => {}, ref, ...other } = inputProps;
  
  return (
    <TextField
      fullWidth
      autoFocus
      InputProps={{
        inputRef: node => {
          ref(node);
          inputRef(node);
        },
        classes: {
          input: classes.input,
        },
      }}
      {...other}
    />
  );
}
  
function renderSuggestion(suggestion, { query, isHighlighted }) {
  const matches = match(suggestion.accountName, query);
  const parts = parse(suggestion.accountName, matches);

  if (suggestion.customerStatus === -2) {
    return (
      <Divider style={{marginTop: 8}}>
        <Chip 
          label={suggestion.accountName}
          avatar={<Avatar src="/logos/freshbooks-225.png" variant="square"/>}
        />
      </Divider>
    );
  } else {
    return (
      <MenuItem selected={isHighlighted} component="div">
        <Grid container spacing={2} direction="row" justifyContent="flex-start">
          <Grid item xs={5}>
            <Typography variant="subtitle1">
            {parts.map((part, index) => {
              if (part.highlight) {
                return (<strong key={index}>{part.text}</strong>);
              } else {
                return (part.text);
              }
            })}
            </Typography>
          </Grid>
          <Grid item xs={7}>
            <Typography variant="subtitle1" data-recording-sensitive>
              {suggestion.contactFirstName} {suggestion.contactLastName}
            </Typography>
          </Grid>
        </Grid>
      </MenuItem>
    );
  }
}

const getSuggestionValue = suggestion => suggestion.accountName;

//these two needed to make warnings go away in autoselect.  Should not be the case according to doc.
function getSectionSuggestions(section) {
  return null;
}

function renderSectionTitle(section) {
  return null;
}

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
    },
    flex: {
      flex: 1,
    },
    paper: {
      padding: theme.spacing(3),
      textAlign: 'left',
    },
    container: {
      position: 'relative',
    },
    suggestionsContainerOpen: {
      position: 'absolute',
      zIndex: 1,
      marginTop: theme.spacing(1),
      left: 0,
      right: 0,
    },
    suggestion: {
      display: 'block',
    },
    suggestionsList: {
      margin: 0,
      padding: 0,
      listStyleType: 'none',
    },
    divider: {
      height: theme.spacing(2),
    },
    configGrid: {
      marginTop: 4,
    },
    button: {
      marginTop: 15,
      marginBottom: 15,
      marginRight: 8
    },
    customText: {
      margin: 8
    },
    searchList: {
      paddingTop: 0,
      width: '100%'
    }
});

class StepCustomer extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      suggestions: [],
      searchP: '',
      customerSearching: false,
      lowCustomerCount: false,
      showEditingFields: false,
      customerCreateDialogOpen: false,
      confirmBudgetChange: false,
      customerAccountId: null,
      customersWithMaps: null,
      customers: null,
      newMappedIds: []
      // extCustomers: []
    }

    this.provisioner = {
      userId:           abEnvironment.getUserAttribute('u_id'),
      accountId:        abEnvironment.getUserAttribute('a_id'),
      accountName:      abEnvironment.getUserAttribute('a_accountName'),
      description:      abEnvironment.getUserAttribute('a_description'),
      contactLastName:  abEnvironment.getUserAttribute('a_lastName'),
      contactFirstName: abEnvironment.getUserAttribute('a_firstName'),
      contactEmail:     abEnvironment.getUserAttribute('a_email'),
      contactPhone:     abEnvironment.getUserAttribute('a_phone'),
      address1:         abEnvironment.getUserAttribute('a_address1'),
      address2:         abEnvironment.getUserAttribute('a_address2'),
      city:             abEnvironment.getUserAttribute('a_city'),
      state:            abEnvironment.getUserAttribute('a_state'),
      country:          abEnvironment.getUserAttribute('a_country'),
      postalCode:       abEnvironment.getUserAttribute('a_postalCode')
    }
    this.customerSelected = false;
  }

  componentDidMount() {
    if (this.props.auth.isAuthenticatedReseller()) {
      this.fetchCustomerCount();
    } else {
      this.setState({showEditingFields: true});
    }
  }

  handleChange = name => event => {
    if (name === 'searchP') {
      this.setState({searchP: event.target.value});
    } 
  };

  handleInputChange = (name, value) => {
    let changeArr = [];
    if (this.props.customer.accountId) {
      if (name === 'accountId') {
        changeArr.push({key: 'accountId', value: value});
      } else {
        changeArr.push({key: 'accountId', value: ''});
      }

      if (name === 'companyName') {
        changeArr.push({key: 'companyName', value: value});
      } else {
        changeArr.push({key: 'companyName', value: ''});
      }

      if (name === 'contactLastName') {
        changeArr.push({key: 'contactLastName', value: value});
      } else {
        changeArr.push({key: 'contactLastName', value: ''});
      }

      if (name === 'contactFirstName') {
        changeArr.push({key: 'contactFirstName', value: value});
      } else {
        changeArr.push({key: 'contactFirstName', value: ''});
      }

      if (name === 'contactEmail') {
        changeArr.push({key: 'contactEmail', value: value});
      } else {
        changeArr.push({key: 'contactEmail', value: ''});
      }

      if (name === 'preApproveSubs') {
        changeArr.push({key: 'preApproveSubs', value: value});
      } else {
        changeArr.push({key: 'preApproveSubs', value: ''});
      }

      if (name === 'rPreApproveSubs') {
        changeArr.push({key: 'rPreApproveSubs', value: value});
      } else {
        changeArr.push({key: 'rPreApproveSubs', value: ''});
      }

      this.props.setParentStatesChanged(changeArr, null);
      this.setState({searchP: ''});
    } else {
      changeArr.push({key: name, value: value});
      this.props.setParentStatesChanged(changeArr, null);
    }
  };

  getCustomerSearchError = () => {  
    if( (this.state.searchP && this.state.searchP.length > 0)
      && (this.state.suggestions === undefined || (this.state.suggestions && this.state.suggestions.length === 0)) 
      && (this.props.customer.companyName !== this.state.searchP)
      && (!this.customerSearching)
    ) {
      return <Typography>No matching customers</Typography>
    } else {
      return '';
    }
  }

  showCustomerCreateDialog = () => {
    this.setState({customerCreateDialogOpen: true});
  };

  handleCustomerCreateClose = (theCust) => {
 
    //is the cust non null and reasonable?
    if (theCust && theCust.accountId && theCust.accountName) {
      //console.log('setting state from customer ' + JSON.stringify(theCust));
      this.setStateFromSelection(theCust);
      let customerLabel = theCust.accountName;
      this.customerSelected = true;
      this.setState({searchP: customerLabel});
    }
    if (abEnvironment.isSegmentActive()) {  window.analytics.track('Subscription create (closed customer create form)'); }

    this.setState({customerCreateDialogOpen: false});
  };

  setStateFromSelection(theCust) {
    const { callPartnerApi } = this.props.auth;

    // Setup for fetching a default project.  This is temporary, but until we allow project selection, we set up fetch with reseller id
    let resellerAccountId = abEnvironment.getUserAttribute("a_id");

    // TODO - if this is coming from an integration, we need to create the customer, and
    // then set the state changed values.  So the approach will be:
    //  - check the passed theCust object to see if it is coming from a new integrated ovject
    //  - set state to indicate that something is happening (spinny)
    //  - create the new customer and integration mapping
    //  - add the mapping info to the state object of cached mappings
    //  - set the passed state
    //  - turn off the spinny 
    if (theCust.fbClientId != null) {
      // Create customer from FreshBooks client.  The link API operation
      // handles this.

      // TODO - set some kind of spinny on
      //
      let abAccountId = null;

      // Lookup in FreshBooks
      let payload = {
        operation: "FreshBooksLinkAccountToClient",
        params: {
          abAccountIdToLink: null,
          fbClientId: theCust.fbClientId,
          mode: 'Link'
        }
      };

      callPartnerApi('appbind/generic',
                     'POST',
                     payload)
      .then(response => {
        return response.json();
      })
      .then(data => {
        abAccountId = data.newAbAccountId;

        if (this.props.useProjects === true) {
          return DefaultProjectFetch.fetch(abAccountId, resellerAccountId).toPromise();
        }
        return null;
      })
      .then(dpf => {
        let changeArr = [];
        let project = null;

        // dpf is null if we are not using projects
        if (dpf != null && dpf.allProjectTs.totalCount > 0) {
          project = dpf.allProjectTs.nodes[0];
        }

        changeArr.push({key: 'accountId', value: abAccountId});
        changeArr.push({key: 'companyName', value: theCust.accountName});
        changeArr.push({key: 'contactLastName', value: theCust.contactLastName});
        changeArr.push({key: 'contactFirstName', value: theCust.contactFirstName});
        changeArr.push({key: 'contactEmail', value: theCust.contactEmail});
        changeArr.push({key: 'preApproveSubs', value: theCust.preApproveSubs});
        changeArr.push({key: 'rPreApproveSubs', value: theCust.rPreApproveSubs});

        let newMap = this.state.newMappedIds;
        newMap.push(theCust.fbClientId);
  
        this.props.setParentStatesChanged(changeArr, project);
        this.setState({searchP: '', newMappedIds: newMap});
      })
      .catch(error => {

      });
    } else {
      Promise.resolve()  
      .then(() => {
        if (this.props.useProjects === true) {
          return DefaultProjectFetch.fetch(theCust.accountId, resellerAccountId).toPromise();
        }
        return null;
      })
      .then(dpf => {
        let changeArr = [];
        let project = null;

        // dpf is null if we are not using projects
        if (dpf != null && dpf.allProjectTs.totalCount > 0) {
          project = dpf.allProjectTs.nodes[0];
        }

        changeArr.push({key: 'accountId', value: theCust.accountId});
        changeArr.push({key: 'companyName', value: theCust.accountName});
        changeArr.push({key: 'contactLastName', value: theCust.contactLastName});
        changeArr.push({key: 'contactFirstName', value: theCust.contactFirstName});
        changeArr.push({key: 'contactEmail', value: theCust.contactEmail});
        changeArr.push({key: 'preApproveSubs', value: theCust.preApproveSubs});
        changeArr.push({key: 'rPreApproveSubs', value: theCust.rPreApproveSubs});

        this.props.setParentStatesChanged(changeArr, project);
      });

      if (theCust.useBudgets === 0) {
        this.setState({confirmBudgetChange: true, customerAccountId: theCust.accountId});
      }
    }
  }

  clearCustomer() {
    let changeArr = [];
    changeArr.push({key: 'accountId', value: ''});
    changeArr.push({key: 'companyName', value: ''});
    changeArr.push({key: 'contactLastName', value: ''});
    changeArr.push({key: 'contactFirstName', value: ''});
    changeArr.push({key: 'contactEmail', value: ''});
    changeArr.push({key: 'preApproveSubs', value: ''});
    changeArr.push({key: 'rPreApproveSubs', value: ''});
    this.props.setParentStatesChanged(changeArr, null);

    this.setState({ suggestions: [], searchP: ''});
  }

  goToNonBudgetBuySubscription() {
    let route = 'subscriptioncreate?accountId=' + this.state.customerAccountId + '&ld=true';
    this.props.history.push(`/${route}`);
  }

  onSuggestionsFetchRequested = ({ value }) => {
    this.setState({customerSearching: true});
    this.fetchCustomers(value);
  }
    
  onSuggestionsClearRequested = () => {
    this.setState({suggestions: []});
    //if customer selected is false, that means the user did not actually select a customer from suggestions list.  Need to reset search box
    if (!this.customerSelected && this.props.customer.accountId) {
      //already a customer selected and it is not reseller
      this.setState({searchP: this.props.customer.accountName});
    /*} else if (!this.customerSelected && this.props.bcUser.usingAccountId === this.provisioner.accountId) {
      //already a selected and it is reseller
      this.setState({searchP: ''});*/
    } else {
      this.customerSelected = false;
    }
  }

  onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) =>{
    // alert(suggestionValue);
    let customerLabel = suggestion.accountName;
    this.customerSelected = true;
    this.setStateFromSelection(suggestion);
    this.setState({searchP: customerLabel, showEditingFields: false});
   } 

  addNewClicked = () =>{
    let changeArr = [];
    changeArr.push({key: 'accountId', value: ''});
    changeArr.push({key: 'companyName', value: ''});
    changeArr.push({key: 'contactLastName', value: ''});
    changeArr.push({key: 'contactFirstName', value: ''});
    changeArr.push({key: 'contactEmail', value: ''});
    changeArr.push({key: 'preApproveSubs', value: ''});
    changeArr.push({key: 'rPreApproveSubs', value: ''});
    this.props.setParentStatesChanged(changeArr, null);

    this.setState({searchP: '', showEditingFields: true});
  } 

   /*
   * Get a customer count (with a search of nothing)
   * 11 is requested because 10 is the max that can fit.
   */
  fetchCustomerCount = () => {
    let theCount = null;
    let customersWithMaps = null;
    let customers = null;
    CustomerFetchCountAndMaps.fetch(this.provisioner.accountId).toPromise()
    .then(cfcwm => {
      theCount = cfcwm.listmappedaccounts.customerCount;
      customersWithMaps = cfcwm.listmappedaccounts.integrations;
      customers = cfcwm.listmappedaccounts.customers;

      if (theCount === 1) {
        // as we're defaulting to the one customer if there is just one, do that here...
        return CustomerFetch.fetch('', 1, 11).toPromise();
      } else {
        return null;
      }
    })
    .then(cf => {
      let customerLabel = null;
      let theCustomers = [];
      if (cf != null) {
        let thenodes = cf.accountsearchbycustomername2.nodes;
        thenodes.forEach(n => {
          if (n.customerStatus !== 2) {
            theCustomers.push(n);
          }
        })

        if (theCustomers.length === 1) {
          customerLabel = theCustomers[0].accountName;
          this.customerSelected = true;
        }
      }

      if (theCount <= 10) {
        if (customerLabel != null && theCustomers.length > 0) {
          this.setStateFromSelection(theCustomers[0]);
          this.setState({searchP: customerLabel, lowCustomerCount: true, customersWithMaps: customersWithMaps, customers: customers});
        } else {
          this.setState({lowCustomerCount: true, customersWithMaps: customersWithMaps, customers: customers});
        }
      } else {
        this.setState({customersWithMaps: customersWithMaps, customers: customers});
      }
    })
    .catch(err => {
      //nothing to do....user will just need to search.
    })
  }

   /*
   * Lookup customers for selection.
   */
  fetchCustomers = (searchParm) => {
    const { callPartnerApi } = this.props.auth;
    let theCustomers = [];

    CustomerFetch.fetch(searchParm, 1, 10).toPromise()
    .then(cf => {
      const thenodes = cf.accountsearchbycustomername2.nodes;
      thenodes.forEach(n => {
        //exclude transferred customers
        if (n.customerStatus !== 2) {
          theCustomers.push(n);
        }
      });

      //
      // If there is an integration active that allows for customer sync, we
      // can allow a reseller to choose a customer from an integrated system.
      //
      let integratedApps = abEnvironment.getUserAttribute('a_integrations');
      if (integratedApps != null && integratedApps === 'FreshBooks') {
        // In the future, we may cache external customer entries in the extCustomer state
        // variable.  For now, all will be live.

        // Lookup in FreshBooks
        let payload = {
          operation: "FreshBooksListClients",
          params: {
            email: null,
            companyName: searchParm
          }
        };
        return callPartnerApi('appbind/generic',
                        'POST',
                        payload);
      } else {
        return null;
      }
    }).then(response => {
      if (response != null) {
        return response.json()
      } else {
        return null;
      }
    }).then(response => {
      if (response != null && response.body != null && response.body.body != null && response.body.body.response != null) {
        let theClients = response.body.body.response.result.clients;

        if (theClients.length > 0) {
          let pushedNodes = [];

          // The raw list needs some processing
          // - omit entries that are already mapped.  To do this, we'll need
          //   to check against customers already listed
          // - sort the list alphabetically
          let customersWithMaps = this.state.customersWithMaps;
          let customers = this.state.customers;

          for (let k=0; k<theClients.length; k++) {
            let clientNode = {
              customerStatus: 1,
              fbClientId: theClients[k].id,
              accountName: theClients[k].organization,
              contactFirstName: theClients[k].fname,
              contactLastName: theClients[k].lname,
              contactEmail: theClients[k].email
            };

            let pushNode = true;
            if (customersWithMaps != null && customersWithMaps.length > 0) {
              // If an integration is active with maps, check if this client is
              // already used in a mapping
              for (let i=0; i<customersWithMaps.length; i++) {
                let integration = customersWithMaps[i];
                if (integration.appName === "FreshBooks") {
                  let maps = integration.maps;
                  if (maps != null && maps.length > 0) {
                    for (let j=0; j<maps.length; j++) {
                      let map = maps[j];
                      let eIdNum = Number(map.entityId);
                      if (eIdNum === theClients[k].id) {
                        // skip this node
                        pushNode = false;
                        break;
                      }
                    }
                  }
                }
                if (pushNode === false) {
                  break;
                }
              }
            } 

            if (pushNode === true && this.state.newMappedIds.length > 0) {
              for (let i=0; i<this.state.newMappedIds.length; i++) {
                let eIdNum = Number(this.state.newMappedIds[k]);
                if (eIdNum === theClients[k].id) {
                  pushNode = false;
                  break;
                }
              }
            }

            // Also check all customers for a match on email
            if (customers != null && customers.length > 0) {
              for (let i=0; i<customers.length; i++) {
                let aCust = customers[i];
                if (aCust.accountEmail === clientNode.contactEmail) {
                  // skup this node
                  pushNode = false;
                  break;
                }
              }
            }

            if (pushNode) {
              pushedNodes.push(clientNode);
            }
          }

          if (pushedNodes.length > 0) {
            // Sort the clients
            pushedNodes.sort (function(a,b) {
              let aStr = '' + a.accountName;
              let bStr = '' + b.accountName;
              // alert('compare ' + aStr + ' to ' + bStr);
              return aStr.localeCompare(bStr);
            });

            // Add a divider
            let dividerNode = {
              customerStatus: -2,
              accountName: 'FreshBooks Clients'
            };
            theCustomers.push(dividerNode);

            // Push all the nodes
            for (let i=0; i<pushedNodes.length; i++) {
              theCustomers.push(pushedNodes[i]);
            }
          }
        }
      }

      this.setState({suggestions: theCustomers, customerSearching: false});
    })
    .catch(err => {
      this.setState({suggestions: [], customerSearching: false});
    })
  }

  shouldAutoRender = (value) => {
    if (this.state.lowCustomerCount || value !== "") {
      return true;
    } else {
      return false;
    }
  }

  isInformationComplete() {
    if (this.props.customer.companyName === '')
      return false;

    if (this.props.customer.contactFirstName === '')
      return false;

    if (this.props.customer.contactLastName === '')
      return false;

    if (this.props.customer.contactEmail === '')
      return false;

    return true;
  }

  isBackDisabled() {
    if (this.props.activeStep === 0)
      return true;

    if (this.props.auth.isAuthenticatedReseller()) {
      return true;
    }

    return false;
  }

  isNextDisabled() {
    if (this.props.activeStep === this.props.assignedStep) {
      if (!this.isInformationComplete()) {
        return true;
      } 
    }
    return false;
  }

  handleNext() {
    //cannot validate since user not logged in or not trying to add new.
    this.props.handleNext();
  }

  render() {
    const classes = this.props.classes;

    let customerLabel = 'Search for existing customer by name';

    let inpValue = this.state.searchP;
    if (inpValue == null) {
      inpValue = '';
    }

    let displayOnly = false;
    if (this.props.activeStep > this.props.assignedStep) {
      displayOnly = true;
    } else if (this.props.displayOnly) {
      displayOnly = true;
    }

    if (!displayOnly) {
      return (

        <Grid container spacing={2}> 
          {this.props.auth.isAuthenticatedReseller() &&
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12}>
                      <Autosuggest
                        renderInputComponent={renderInputComponent}
                        suggestions={this.state.suggestions}
                        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                        onSuggestionSelected={this.onSuggestionSelected}
                        getSuggestionValue={getSuggestionValue}
                        renderSuggestion={renderSuggestion}
                        shouldRenderSuggestions={this.shouldAutoRender}
                        multiSection={false}
                        renderSectionTitle={renderSectionTitle}
                        getSectionSuggestions={getSectionSuggestions}
                        inputProps={{
                          classes,
                          placeholder: customerLabel,
                          value: inpValue,
                          onChange: this.handleChange('searchP')
                        }}
                        theme={{
                          input: classes.input,
                          container: classes.container,
                          suggestionsContainerOpen: classes.suggestionsContainerOpen,
                          suggestionsList: classes.suggestionsList,
                          suggestion: classes.suggestion,
                        }}
                        renderSuggestionsContainer={options => (
                          <Paper {...options.containerProps} square>
                            {options.children}
                          </Paper>
                        )}                   
                      /><Search color="disabled" style={{float: "right", marginTop: -26}}/>
                    </Grid>
                    {!this.state.customerSearching && 
                    <Grid item xs={12}>
                      {this.getCustomerSearchError()}
                    </Grid>}
                    <Grid item><Typography variant="body1">or</Typography></Grid>
                    <Grid item>
                      <Tooltip title="Add a new customer">
                        <Button
                          color="primary"
                          variant="outlined"
                          onClick={() => {this.showCustomerCreateDialog();}}
                        >    
                          Add New Customer
                        </Button>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Grid>

                {this.props.customer.accountId && this.state.showEditingFields === false &&
                <React.Fragment>
                  <Grid item xs={12}><Divider/></Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" style={AppBindStyles.boldLabel}>
                      {this.props.customer.companyName}
                    </Typography>
                    <Typography variant="body2">
                      {this.props.customer.contactFirstName}&nbsp;{this.props.customer.contactLastName}<br/>
                      {this.props.customer.contactEmail}
                    </Typography>
                  </Grid>
                </React.Fragment>}
              </Grid>
            </Paper>
          </Grid>}          

          {/* Dialogs */}
          <CustomerCreateDialog
            title="Create customer"
            open={this.state.customerCreateDialogOpen}
            onClose={this.handleCustomerCreateClose}
          >
            <CustomerCreateForm
              auth={this.props.auth}
              onClose={this.handleCustomerCreateClose}/>
          </CustomerCreateDialog>

          <BudgetChangeConfirmationDialog
              open={this.state.confirmBudgetChange}
              onClose={() => { this.setState({confirmBudgetChange:false});}}
              onContinue={() => {this.handleNext() }}
              onCancel={() => { this.clearCustomer() }}
              onBuySubscription={() => {this.goToNonBudgetBuySubscription()}}/>


          {/* Next/Back buttons */}
          {this.props.activeStep === this.props.assignedStep &&
          <Grid item xs={12}>
            <Button
              disabled={this.isBackDisabled()}
              color="primary" variant="outlined"
              onClick={() => this.props.handleBack()}
              style={AppBindStyles.bottomButton}>
              Back
            </Button>
            <Button
              disabled={this.isNextDisabled()}
              color="primary" variant="contained"
              onClick={() => this.handleNext()}
              style={AppBindStyles.bottomButton}>
              Next
            </Button>
          </Grid>}
        </Grid>       
      );  
    } else {
      //display only
      return (
        <React.Fragment>
          <Typography variant="body1" style={AppBindStyles.boldLabel}>
            {this.props.customer.companyName}
          </Typography>
          <Typography variant="body2">
            {this.props.customer.contactFirstName}&nbsp;{this.props.customer.contactLastName}<br/>
            {this.props.customer.contactEmail}
          </Typography>
        </React.Fragment>
      );
    }
  }
}

export default withStyles(styleSheet)(StepCustomer);
